import React, { useState, useEffect, useCallback } from 'react';
import {
  getExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
  getFinanceHistory,
} from '../../../services/adminService';
import {
  Receipt, RefreshCw, AlertCircle, Plus, Pencil, Trash2, Check, X,
  TrendingUp, Wallet, Banknote, CalendarCheck, Scale,
} from 'lucide-react';

const fmtCOP = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);

// Mes actual en Bogotá como 'YYYY-MM' (Colombia es UTC-5 fijo).
const currentMonth = () => new Date(Date.now() - 5 * 3600 * 1000).toISOString().slice(0, 7);

// Suma n meses a un 'YYYY-MM'.
const addMonthsStr = (m, n) => {
  const [y, mo] = m.split('-').map(Number);
  return new Date(Date.UTC(y, mo - 1 + n, 1)).toISOString().slice(0, 7);
};

// '2026-10' -> 'octubre 2026'
const fmtMonth = (m) => {
  const [y, mo] = m.split('-').map(Number);
  return new Date(Date.UTC(y, mo - 1, 1, 12)).toLocaleDateString('es-CO', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

// Clases completas (no interpoladas) para que Tailwind las detecte al compilar.
const CARD_COLORS = {
  teal:   { bg: 'bg-teal-100',   fg: 'text-teal-600' },
  green:  { bg: 'bg-green-100',  fg: 'text-green-600' },
  yellow: { bg: 'bg-yellow-100', fg: 'text-yellow-600' },
  red:    { bg: 'bg-red-100',    fg: 'text-red-600' },
};

const SummaryCard = ({ Icon, label, value, color = 'teal', valueClass = 'text-gray-900' }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5">
    <div className="flex items-center gap-3 mb-1">
      <div className={`w-9 h-9 rounded-full ${CARD_COLORS[color].bg} flex items-center justify-center`}>
        <Icon className={`w-4 h-4 ${CARD_COLORS[color].fg}`} />
      </div>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
    <p className={`text-2xl font-bold ${valueClass}`}>{value}</p>
  </div>
);

// ─── Fila de gasto con edición en línea ───────────────────────────────────────
const ExpenseRow = ({ expense, onChanged, onError }) => {
  const [editing, setEditing] = useState(false);
  const [desc,    setDesc]    = useState(expense.description);
  const [amount,  setAmount]  = useState(String(expense.amount));
  const [busy,    setBusy]    = useState(false);

  const save = async () => {
    setBusy(true); onError(null);
    try {
      await updateExpense(expense._id, { description: desc, amount: Number(amount) });
      setEditing(false);
      await onChanged();
    } catch (e) {
      onError(e?.response?.data?.message || 'Error al guardar el gasto');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar el gasto "${expense.description}"?`)) return;
    setBusy(true); onError(null);
    try {
      await deleteExpense(expense._id);
      await onChanged();
    } catch (e) {
      onError(e?.response?.data?.message || 'Error al eliminar el gasto');
      setBusy(false);
    }
  };

  const cancel = () => {
    setDesc(expense.description);
    setAmount(String(expense.amount));
    setEditing(false);
  };

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 text-gray-900">
        {editing ? (
          <input value={desc} onChange={(e) => setDesc(e.target.value)} maxLength={200}
            className="w-full border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:border-teal-400" />
        ) : expense.description}
      </td>
      <td className="px-4 py-3 text-gray-900 tabular-nums">
        {editing ? (
          <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)}
            className="w-36 border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:border-teal-400" />
        ) : fmtCOP(expense.amount)}
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        {editing ? (
          <>
            <button onClick={save} disabled={busy} title="Guardar"
              className="p-1.5 text-teal-600 hover:bg-teal-50 rounded-lg disabled:opacity-50"><Check className="w-4 h-4" /></button>
            <button onClick={cancel} disabled={busy} title="Cancelar"
              className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg disabled:opacity-50"><X className="w-4 h-4" /></button>
          </>
        ) : (
          <>
            <button onClick={() => setEditing(true)} disabled={busy} title="Editar"
              className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg disabled:opacity-50"><Pencil className="w-4 h-4" /></button>
            <button onClick={remove} disabled={busy} title="Eliminar"
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"><Trash2 className="w-4 h-4" /></button>
          </>
        )}
      </td>
    </tr>
  );
};

// ─── Sección principal ────────────────────────────────────────────────────────
export default function FinanceSection() {
  const [month,    setMonth]    = useState(currentMonth());
  const [expenses, setExpenses] = useState([]);
  const [total,    setTotal]    = useState(0);
  const [history,  setHistory]  = useState([]);
  const [oldSummary, setOldSummary] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [actionError, setActionError] = useState(null);

  const [desc,   setDesc]   = useState('');
  const [amount, setAmount] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // Si el mes elegido queda fuera de los últimos 12, se pide aparte su fila.
      const outOfRange = month < addMonthsStr(currentMonth(), -11);
      const [exp, hist, extra] = await Promise.all([
        getExpenses(month),
        getFinanceHistory(12),
        outOfRange ? getFinanceHistory(1, month) : Promise.resolve([]),
      ]);
      setExpenses(exp.expenses);
      setTotal(exp.total);
      setHistory(hist);
      setOldSummary(extra[0] || null);
    } catch (e) {
      setError(e?.response?.data?.message || 'Error al cargar gastos y balance');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true); setActionError(null);
    try {
      await createExpense({ month, description: desc, amount: Number(amount) });
      setDesc(''); setAmount('');
      await load();
    } catch (err) {
      setActionError(err?.response?.data?.message || 'Error al agregar el gasto');
    } finally {
      setAdding(false);
    }
  };

  // Resumen del mes seleccionado: sale del histórico o, si es más antiguo que
  // los últimos 12 meses, de su fila pedida aparte.
  const summary = history.find(h => h.month === month) || oldSummary;
  const gross   = summary?.grossIncome || 0;
  const share   = summary?.doctorShare || 0;
  const netInc  = gross - share;
  const balance = netInc - total;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Gastos y balance</h2>
          <p className="text-sm text-gray-500">Registra los gastos del mes y consulta el histórico de ingresos, egresos y citas.</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-teal-400" />
          <button onClick={load} disabled={loading}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-teal-600 border border-gray-200 rounded-lg px-3 py-1.5 hover:border-teal-300 transition-colors disabled:opacity-40">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />Actualizar
          </button>
        </div>
      </div>

      {error ? (
        <div className="flex flex-col items-center justify-center h-48 gap-3 text-red-500">
          <AlertCircle className="w-8 h-8" />
          <p className="text-sm">{error}</p>
          <button onClick={load} className="text-sm text-teal-600 hover:underline">Reintentar</button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            <SummaryCard Icon={TrendingUp}    label="Ingresos brutos" value={fmtCOP(gross)}   color="green" />
            <SummaryCard Icon={Wallet}        label="Pago a médicos"  value={fmtCOP(share)}   color="yellow" />
            <SummaryCard Icon={Banknote}      label="Ingresos netos"  value={fmtCOP(netInc)}  color="teal" />
            <SummaryCard Icon={Receipt}       label="Gastos"          value={fmtCOP(total)}   color="red" />
            <SummaryCard Icon={Scale}         label="Balance"         value={fmtCOP(balance)} color={balance < 0 ? 'red' : 'teal'}
              valueClass={balance < 0 ? 'text-red-600' : 'text-gray-900'} />
            <SummaryCard Icon={CalendarCheck} label="Citas"           value={summary?.appointments ?? 0} color="teal" />
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <form onSubmit={handleAdd} className="flex flex-wrap items-end gap-3 p-4 border-b border-gray-100">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">Descripción</label>
                <input value={desc} onChange={(e) => setDesc(e.target.value)} maxLength={200} required
                  placeholder="Ej. Hosting, publicidad, nómina…"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-400" />
              </div>
              <div className="w-44">
                <label className="block text-xs font-medium text-gray-500 mb-1">Valor (COP)</label>
                <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} required
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-400" />
              </div>
              <button type="submit" disabled={adding}
                className="inline-flex items-center gap-1.5 text-sm px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 font-medium">
                {adding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Agregar
              </button>
            </form>

            {actionError && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-4 py-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />{actionError}
              </div>
            )}

            {loading ? (
              <div className="animate-pulse p-4 space-y-3">
                {[...Array(3)].map((_, i) => <div key={i} className="h-10 bg-gray-100 rounded-lg" />)}
              </div>
            ) : expenses.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <Receipt className="w-10 h-10 mb-3 text-gray-200" />
                <p className="text-sm">No hay gastos registrados en {fmtMonth(month)}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Descripción', 'Valor', ''].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {expenses.map(exp => (
                      <ExpenseRow key={exp._id} expense={exp} onChanged={load} onError={setActionError} />
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td className="px-4 py-3 font-semibold text-gray-900">Total del mes</td>
                      <td className="px-4 py-3 font-semibold text-gray-900 tabular-nums">{fmtCOP(total)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-900">Histórico</h3>
              <p className="text-xs text-gray-500">Últimos 12 meses. Haz clic en un mes para verlo arriba.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Mes', 'Citas', 'Ingresos brutos', 'Pago a médicos', 'Ingresos netos', 'Gastos', 'Balance'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {history.map(h => (
                    <tr key={h.month} onClick={() => setMonth(h.month)}
                      className={`cursor-pointer hover:bg-gray-50 ${h.month === month ? 'bg-teal-50' : ''}`}>
                      <td className="px-4 py-3 font-medium text-gray-900 capitalize">{fmtMonth(h.month)}</td>
                      <td className="px-4 py-3 text-gray-600 tabular-nums">{h.appointments}</td>
                      <td className="px-4 py-3 text-gray-900 tabular-nums">{fmtCOP(h.grossIncome)}</td>
                      <td className="px-4 py-3 text-gray-600 tabular-nums">{fmtCOP(h.doctorShare)}</td>
                      <td className="px-4 py-3 text-gray-900 tabular-nums">{fmtCOP(h.netIncome)}</td>
                      <td className="px-4 py-3 text-gray-600 tabular-nums">{fmtCOP(h.expenses)}</td>
                      <td className={`px-4 py-3 font-medium tabular-nums ${h.balance < 0 ? 'text-red-600' : 'text-gray-900'}`}>{fmtCOP(h.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
